import React, { useEffect, useRef } from 'react';
import { Animated, View, ViewStyle } from 'react-native';
import colors from '@theme/colors';

export interface SkeletonProps {
  height?: number | string;
  width?: number | string;
  borderRadius?: number;
  animation?: 'wave' | 'pulse' | 'none';
  style?: ViewStyle;
  testID?: string;
  // Additional props for compatibility
  h?: number | string;
  w?: number | string;
  px?: number | string;
  py?: number | string;
  margin?: number | string;
  marginX?: number | string;
  marginY?: number | string;
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  padding?: number | string;
  paddingX?: number | string;
  paddingY?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
}

const Skeleton: React.FC<SkeletonProps> = ({
  height,
  width = '100%',
  borderRadius = 4,
  animation = 'wave',
  style,
  testID,
  // Native-base compatibility props
  h,
  w,
  px,
  py,
  margin,
  marginX,
  marginY,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  padding,
  paddingX,
  paddingY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  ...otherProps
}) => {
  const animatedValue = useRef(new Animated.Value(0)).current;
  const pulseValue = useRef(new Animated.Value(1)).current;

  // Use h prop if height is not provided (native-base compatibility)
  const finalHeight = height || h || 20;
  // Use w prop if width is not provided (native-base compatibility)
  const finalWidth = width || w || '100%';

  useEffect(() => {
    if (animation === 'wave') {
      const animationLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(animatedValue, {
            toValue: 1,
            duration: 1200,
            useNativeDriver: true,
          }),
          Animated.timing(animatedValue, {
            toValue: 0,
            duration: 1200,
            useNativeDriver: true,
          }),
        ]),
      );
      animationLoop.start();
      return () => animationLoop.stop();
    } else if (animation === 'pulse') {
      const pulseLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseValue, {
            toValue: 0.6,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(pulseValue, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
        ]),
      );
      pulseLoop.start();
      return () => pulseLoop.stop();
    }
  }, [animation, animatedValue, pulseValue]);

  // Combine all style-related props into a single style object
  const combinedStyle: ViewStyle = {
    ...style,
    ...(margin !== undefined && { margin }),
    ...(marginX !== undefined && { marginHorizontal: marginX }),
    ...(marginY !== undefined && { marginVertical: marginY }),
    ...(marginTop !== undefined && { marginTop }),
    ...(marginBottom !== undefined && { marginBottom }),
    ...(marginLeft !== undefined && { marginLeft }),
    ...(marginRight !== undefined && { marginRight }),
    ...(padding !== undefined && { padding }),
    ...(paddingX !== undefined && { paddingHorizontal: paddingX }),
    ...(paddingY !== undefined && { paddingVertical: paddingY }),
    ...(paddingTop !== undefined && { paddingTop }),
    ...(paddingBottom !== undefined && { paddingBottom }),
    ...(paddingLeft !== undefined && { paddingLeft }),
    ...(paddingRight !== undefined && { paddingRight }),
  };

  const getAnimationStyle = () => {
    if (animation === 'wave') {
      return {
        opacity: animatedValue.interpolate({
          inputRange: [0, 1],
          outputRange: [0.3, 0.8],
        }),
      };
    } else if (animation === 'pulse') {
      return {
        opacity: pulseValue,
      };
    }
    return {};
  };

  return (
    <Animated.View
      testID={testID}
      style={[
        {
          height: finalHeight,
          width: finalWidth,
          borderRadius,
          backgroundColor: colors.neutral.lightMist || '#E1E9EE',
          overflow: 'hidden',
        },
        getAnimationStyle(),
        combinedStyle,
      ]}
      {...otherProps}>
      {animation === 'wave' && (
        <Animated.View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            transform: [
              {
                translateX: animatedValue.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-100, 100],
                }),
              },
            ],
          }}
        />
      )}
    </Animated.View>
  );
};

// Skeleton.Text component for native-base compatibility
export const SkeletonText: React.FC<SkeletonProps> = props => <Skeleton height={16} borderRadius={2} {...props} />;

export default Skeleton;
